import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, User, MapPin, Euro, Camera, Upload, X } from 'lucide-react';
import api from '../utils/axiosConfig';

const Onboarding = ({ user, onComplete, onSkip }) => {
  const [step, setStep] = useState(1); // Start at step 1 (Personal Info)
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState({
    firstname: user?.name?.split(' ')[0] || '',
    lastname: user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    defaultRole: 'TENANT',
    signupType: user?.authProvider === 'google' ? 'GOOGLE' : 'EMAIL',
    isNewUser: true,
    gender: '',
    dob: '',
    location: null,
    budget: { min: '', max: '' },
    currency_preference: user?.currency_preference || 'USD',
    area_unit_preference: user?.area_unit_preference || 'sqm',
    profileImage: user?.picture || null
  });

  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const locationInputRef = useRef(null);

  const avatarOptions = [
    { emoji: '👤', name: 'Default' },
    { emoji: '😊', name: 'Happy' },
    { emoji: '🎭', name: 'Theater' },
    { emoji: '🎨', name: 'Artist' },
    { emoji: '🎵', name: 'Music' },
    { emoji: '⚽', name: 'Sports' },
    { emoji: '📚', name: 'Bookworm' },
    { emoji: '🌍', name: 'Traveler' }
  ];

  const steps = [
    { title: 'Personal Info', subtitle: 'Tell us about yourself', icon: User },
    { title: 'Location', subtitle: 'Where are you looking?', icon: MapPin },
    { title: 'Budget', subtitle: 'What\'s your budget?', icon: Euro },
    { title: 'Profile Picture', subtitle: 'Add your photo or choose an avatar', icon: Camera }
  ];

  // City normalization: map smaller cities to larger metropolitan areas
  const normalizeCity = (cityName) => {
    const cityMap = {
      'Haarlem': 'Amsterdam',
      'Amstelveen': 'Amsterdam',
      'Zaandam': 'Amsterdam',
      'Hoofddorp': 'Amsterdam',
      'Delft': 'Rotterdam',
      'Schiedam': 'Rotterdam',
      'Dordrecht': 'Rotterdam',
      'Gouda': 'Rotterdam',
      'Leiden': 'The Hague',
      'Rijswijk': 'The Hague',
      'Zoetermeer': 'The Hague',
      'Nieuwegein': 'Utrecht',
      'Amersfoort': 'Utrecht',
      'Veldhoven': 'Eindhoven',
      'Helmond': 'Eindhoven',
      'Haren': 'Groningen',
      'Assen': 'Groningen'
    };
    return cityMap[cityName] || cityName;
  };

  // Fetch Google Places Autocomplete suggestions via backend proxy
  const fetchPlacesSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setLocationSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      console.log('Fetching places suggestions for query:', query);
      const response = await api.get('/api/places/autocomplete', {
        params: { input: query }
      });
      
      console.log('Places API response:', response.data);
      console.log('Response status:', response.status);
      
      if (response.data && response.data.predictions) {
        console.log('Setting suggestions:', response.data.predictions.length);
        setLocationSuggestions(response.data.predictions);
        setShowSuggestions(true);
      } else if (response.data && response.data.error) {
        console.error('Places API error:', response.data.error);
        setLocationSuggestions([]);
        setShowSuggestions(false);
      } else {
        console.log('No predictions in response, response data:', response.data);
        setLocationSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching places:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      setLocationSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  // Calculate dropdown position when input is focused or suggestions change
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (locationInputRef.current) {
        const rect = locationInputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    };

    // Calculate position whenever input is focused or suggestions are available
    if (showSuggestions) {
      updateDropdownPosition();
      // Update position on scroll/resize
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [showSuggestions, locationSuggestions]);

  // Handle location input change with debounce
  useEffect(() => {
    if (!locationQuery || locationQuery.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchPlacesSuggestions(locationQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [locationQuery, fetchPlacesSuggestions]);

  // Handle place selection
  const handlePlaceSelect = async (prediction) => {
    try {
      const detailsResponse = await api.post('/api/places/details', {
        place_id: prediction.place_id
      });
      
      if (detailsResponse.data.result) {
        const place = detailsResponse.data.result;
        const addressComponents = place.address_components || [];
        
        const getComponent = (type) => 
          addressComponents.find(c => c.types.includes(type));

        const cityComponent = getComponent('locality') || getComponent('administrative_area_level_2');
        const stateComponent = getComponent('administrative_area_level_1');
        const countryComponent = getComponent('country');

        const cityName = cityComponent?.long_name || place.name || prediction.description.split(',')[0];
        const normalizedCity = normalizeCity(cityName);
        
        const locationData = {
          city: normalizedCity,
          state: stateComponent?.long_name || '',
          state_code: stateComponent?.short_name || '',
          country: countryComponent?.long_name || '',
          country_code: countryComponent?.short_name || '',
          lat: place.geometry.location.lat,
          lng: place.geometry.location.lng,
          place_id: prediction.place_id,
          label: prediction.description
        };
        
        setProfile(prev => ({ ...prev, location: locationData }));
        setLocationWarning(false);
        setLocationQuery(prediction.description);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile(prev => ({ ...prev, profileImage: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarSelect = (emoji) => {
    // Convert emoji to a simple data URI (or use a service)
    setProfile(prev => ({ ...prev, profileImage: `emoji:${emoji}` }));
  };

  const [locationWarning, setLocationWarning] = useState(false);

  const handleNext = async () => {
    if (step < steps.length) {
      setStep(step + 1);
    } else {
      if (!profile.location) {
        setLocationWarning(true);
        setStep(2);
        return;
      }
      try {
        const formattedDob = profile.dob ? new Date(profile.dob).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        }) : '';
        
        const finalProfile = {
          ...profile,
          dob: formattedDob,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        if (onComplete) {
          await onComplete(finalProfile);
        }
      } catch (error) {
        console.error('Error completing onboarding:', error);
      }
    }
  };

  const handleSkip = () => {
    // Skip onboarding without saving any data
    console.log('Skipping onboarding - no data will be saved');
    if (onSkip) {
      onSkip();
    } else {
      console.warn('onSkip callback is not defined');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto relative"
      >
        {/* Close Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleSkip}
          className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100 z-10"
          aria-label="Close onboarding"
        >
          <X className="w-5 h-5" />
        </motion.button>

        {/* Progress */}
        <div className="mb-6 pr-12">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-medium text-gray-600">Step {step} of {steps.length}</span>
            <span className="text-xs font-medium text-gray-900">{Math.round((step / steps.length) * 100)}%</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gray-900"
              initial={{ width: 0 }}
              animate={{ width: `${(step / steps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{steps[0].title}</h2>
                <p className="text-sm text-gray-600">{steps[0].subtitle}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={profile.firstname}
                    onChange={(e) => setProfile({ ...profile, firstname: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={profile.lastname}
                    onChange={(e) => setProfile({ ...profile, lastname: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                <input
                  type="date"
                  value={profile.dob}
                  onChange={(e) => setProfile({ ...profile, dob: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <div className="grid grid-cols-3 gap-3">
                  {['Male', 'Female', 'Other'].map((gender) => (
                    <motion.button
                      key={gender}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setProfile({ ...profile, gender })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        profile.gender === gender
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {gender}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{steps[1].title} <span className="text-red-500">*</span></h2>
                <p className="text-sm text-gray-600">{steps[1].subtitle}</p>
                {locationWarning && !profile.location && (
                  <p className="text-sm text-red-500 mt-2 font-medium">Please select a location to continue</p>
                )}
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Where are you looking for housing? <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    ref={locationInputRef}
                    type="text"
                    value={locationQuery}
                    onChange={(e) => {
                      setLocationQuery(e.target.value);
                      if (profile.location) {
                        setProfile(prev => ({ ...prev, location: null }));
                      }
                      setShowSuggestions(true);
                      if (locationInputRef.current) {
                        const rect = locationInputRef.current.getBoundingClientRect();
                        setDropdownPosition({
                          top: rect.bottom + 4,
                          left: rect.left,
                          width: rect.width
                        });
                      }
                    }}
                    onFocus={() => {
                      if (locationSuggestions.length > 0) setShowSuggestions(true);
                      if (locationInputRef.current) {
                        const rect = locationInputRef.current.getBoundingClientRect();
                        setDropdownPosition({
                          top: rect.bottom + 4,
                          left: rect.left,
                          width: rect.width
                        });
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="e.g. Amsterdam, New York, London..."
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900"
                  />
                  {profile.location && (
                    <button
                      type="button"
                      onClick={() => {
                        setProfile(prev => ({ ...prev, location: null }));
                        setLocationQuery('');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {typeof document !== 'undefined' && createPortal(
                  <AnimatePresence>
                    {showSuggestions && locationSuggestions.length > 0 && (
                      <motion.div
                        key="location-dropdown"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="fixed z-[9999] bg-white border-2 border-gray-200 rounded-lg shadow-2xl max-h-60 overflow-y-auto"
                        style={{
                          top: `${dropdownPosition.top || 0}px`,
                          left: `${dropdownPosition.left || 0}px`,
                          width: `${dropdownPosition.width || (locationInputRef.current?.offsetWidth || 400)}px`,
                          pointerEvents: 'auto'
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {locationSuggestions.map((prediction) => (
                          <button
                            key={prediction.place_id}
                            type="button"
                            onClick={() => handlePlaceSelect(prediction)}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 first:rounded-t-lg last:rounded-b-lg"
                          >
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <div>
                                <div className="font-medium text-gray-900">{prediction.structured_formatting?.main_text || prediction.description}</div>
                                {prediction.structured_formatting?.secondary_text && (
                                  <div className="text-sm text-gray-500 mt-0.5">{prediction.structured_formatting.secondary_text}</div>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>,
                  document.body
                )}
                
              </div>
            </motion.div>
          )}

          {/* Step 3: Budget */}
          {step === 3 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{steps[2].title}</h2>
                <p className="text-sm text-gray-600">{steps[2].subtitle}</p>
              </div>
              
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                  <div className="sm:w-2/5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                    <div className="grid grid-cols-2 gap-3">
                      {['USD', 'EUR'].map((curr) => (
                        <motion.button
                          key={curr}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setProfile({ ...profile, currency_preference: curr })}
                          className={`p-3 rounded-lg border-2 font-medium transition-all ${
                            profile.currency_preference === curr
                              ? 'border-gray-900 bg-gray-900 text-white'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {curr === 'USD' ? '$ Dollars' : '€ Euros'}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 sm:w-3/5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Min ({profile.currency_preference === 'EUR' ? '€' : '$'})
                      </label>
                      <input
                        type="number"
                        value={profile.budget.min}
                        onChange={(e) => setProfile({ ...profile, budget: { ...profile.budget, min: e.target.value } })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900"
                        placeholder="800"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max ({profile.currency_preference === 'EUR' ? '€' : '$'})
                      </label>
                      <input
                        type="number"
                        value={profile.budget.max}
                        onChange={(e) => setProfile({ ...profile, budget: { ...profile.budget, max: e.target.value } })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900"
                        placeholder="1500"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Area unit for listings</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['sqm', 'sqft'].map((unit) => (
                      <motion.button
                        key={unit}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setProfile({ ...profile, area_unit_preference: unit })}
                        className={`p-3 rounded-lg border-2 font-medium transition-all ${
                          profile.area_unit_preference === unit
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {unit === 'sqm' ? 'm² (square meters)' : 'sqft (square feet)'}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Profile Picture */}
          {step === 4 && (
            <motion.div
              key="step6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{steps[3].title}</h2>
                <p className="text-sm text-gray-600">{steps[3].subtitle}</p>
              </div>
              
              <div className="space-y-6">
                {/* Current Image Preview */}
                {profile.profileImage && !profile.profileImage.startsWith('emoji:') && (
                  <div className="flex justify-center">
                    <div className="relative">
                      <img
                        src={profile.profileImage}
                        alt="Profile"
                        className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                      />
                      <button
                        onClick={() => setProfile({ ...profile, profileImage: null })}
                        className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Upload Button */}
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:border-gray-400 transition-all flex items-center justify-center gap-2"
                  >
                    <Upload className="w-5 h-5" />
                    Upload Photo
                  </motion.button>
                </div>

                <div className="text-center text-sm text-gray-500">or</div>

                {/* Avatar Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Choose an Avatar</label>
                  <div className="grid grid-cols-4 gap-3">
                    {avatarOptions.map((avatar) => (
                      <motion.button
                        key={avatar.name}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleAvatarSelect(avatar.emoji)}
                        className={`p-4 rounded-lg border-2 text-3xl transition-all ${
                          profile.profileImage === `emoji:${avatar.emoji}`
                            ? 'border-gray-900 bg-gray-900'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        {avatar.emoji}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        {step > 0 && (
          <div className="mt-8 flex items-center justify-between">
            {step > 1 && (
              <motion.button
                whileHover={{ x: -3 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back
              </motion.button>
            )}
            {step === 1 && <div />}
            
            <div className="flex items-center gap-3">
              {step !== 2 && step < steps.length && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setStep(step + 1)}
                  className="px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors text-sm font-medium"
                  type="button"
                >
                  Skip
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNext();
                }}
                className="px-6 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
                type="button"
              >
                {step === steps.length ? 'Complete' : 'Next'}
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Onboarding;
